import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '@/components/feature/LanguageSelector';

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const navLinks = [
    { labelKey: 'nav.home', href: '#inicio' },
    { labelKey: 'nav.catalog', href: '#catalogo' },
    { labelKey: 'nav.import', href: '#importacion' },
    { labelKey: 'nav.featured', href: '#destacados' },
    { labelKey: 'nav.contact', href: '#contacto' },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-[#050505]/95 backdrop-blur-xl border-b border-purple-900/30'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#inicio" onClick={() => handleNavClick('#inicio')} className="flex items-center gap-3 cursor-pointer group">
            <img
              src="https://static.readdy.ai/image/0c7bdc23c0509608cd59066307fba6e0/19dd2d639fbad16ddbf690588f9b26c2.png"
              alt="ZS Cars Import Logo"
              className="h-14 w-auto object-contain flex-shrink-0"
            />
            <div className="flex flex-col leading-none">
              <span
                className="font-display text-white tracking-widest uppercase"
                style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: '1.55rem',
                  letterSpacing: '0.12em',
                  textShadow: '0 0 18px rgba(168, 85, 247, 0.55), 0 0 36px rgba(155, 48, 255, 0.25)',
                  lineHeight: 1.05,
                }}
              >
                ZS Cars Import
              </span>
              <span
                className="text-purple-400/80 uppercase tracking-[0.22em]"
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: '0.52rem',
                  fontWeight: 500,
                  letterSpacing: '0.22em',
                  lineHeight: 1.2,
                  textShadow: '0 0 10px rgba(168, 85, 247, 0.35)',
                }}
              >
                Importación de Vehículos Premium
              </span>
            </div>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.labelKey}
                onClick={() => handleNavClick(link.href)}
                className="font-label text-white/65 hover:text-white text-xs tracking-widest uppercase transition-colors duration-300 cursor-pointer whitespace-nowrap relative group"
              >
                {t(link.labelKey)}
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-purple-500 group-hover:w-full transition-all duration-300" />
              </button>
            ))}
          </div>

          {/* Right side: Language + CTA */}
          <div className="hidden md:flex items-center gap-3">
            <LanguageSelector />
            <button
              onClick={() => handleNavClick('#contacto')}
              className="gradient-purple font-label text-white text-xs tracking-widest uppercase px-6 py-3 rounded-full glow-purple hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              {t('nav.cta')}
            </button>
          </div>

          {/* Mobile: Language + Hamburger */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageSelector />
            <button
              className="text-white w-8 h-8 flex items-center justify-center cursor-pointer"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <i className={`ri-${menuOpen ? 'close' : 'menu'}-line text-2xl`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-[#050505]/98 backdrop-blur-xl border-t border-purple-900/30 px-4 py-6">
          <div className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <button
                key={link.labelKey}
                onClick={() => handleNavClick(link.href)}
                className="text-white/70 hover:text-white text-sm font-medium tracking-widest uppercase transition-colors duration-300 text-left cursor-pointer whitespace-nowrap"
              >
                {t(link.labelKey)}
              </button>
            ))}
            <button
              onClick={() => handleNavClick('#contacto')}
              className="gradient-purple text-white text-sm font-semibold tracking-wider uppercase px-6 py-3 rounded-full mt-2 cursor-pointer whitespace-nowrap"
            >
              {t('nav.cta')}
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}